// Formato de montos del módulo de inventario según el separador decimal elegido
// por el owner en "Monedas" (',' = decimales con coma / miles con punto; '.' = al revés).
// Homogeneiza cómo el usuario ingresa y ve los números independientemente del país.

const normalizeSep = (sep) => (sep === "." ? "." : ",");

// Convierte un string ingresado por el usuario (según su separador) a Number.
// Ej: parseAmount("1.000.000,50", ",") -> 1000000.5
//     parseAmount("1,000,000.50", ".") -> 1000000.5
export const parseAmount = (input, sep = ",") => {
  if (input === undefined || input === null) return null;
  if (typeof input === "number") return Number.isFinite(input) ? input : null;
  let str = String(input).trim();
  if (!str) return null;

  const decimalSep = normalizeSep(sep);
  const thousandsSep = decimalSep === "," ? "." : ",";

  // Quitar separadores de miles y normalizar el decimal a '.'
  str = str.split(thousandsSep).join("");
  str = str.split(decimalSep).join(".");
  // Descartar cualquier caracter que no sea dígito, punto o signo.
  str = str.replace(/[^0-9.\-]/g, "");

  const n = Number(str);
  return Number.isFinite(n) ? n : null;
};

// Formatea un Number para mostrar según el separador elegido.
export const formatAmount = (value, sep = ",", { decimals } = {}) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";

  const decimalSep = normalizeSep(sep);
  const locale = decimalSep === "," ? "de-DE" : "en-US"; // de-DE: miles '.', decimales ','

  const opts =
    decimals === undefined
      ? { minimumFractionDigits: 0, maximumFractionDigits: 2 }
      : { minimumFractionDigits: decimals, maximumFractionDigits: decimals };

  try {
    return new Intl.NumberFormat(locale, opts).format(n);
  } catch {
    return String(n);
  }
};

// Formatea un monto con la moneda como prefijo (ej. "AR$ 1.000.000").
export const formatMoney = (value, currency, sep = ",") => {
  const amount = formatAmount(value, sep);
  if (amount === "") return "";
  return currency ? `${currency} ${amount}` : amount;
};

// --- Conversión entre moneda predeterminada y alternativa ---
// `rate` = cuántas unidades de la moneda predeterminada vale 1 de la alternativa
// (ej. 1 U$S = 2000 AR$). Si el insumo tiene el costo cargado en una sola moneda,
// el de la otra se calcula con el tipo de cambio.
const toNum = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
const validRate = (rate) => {
  const r = toNum(rate);
  return r && Number.isFinite(r) && r > 0 ? r : null;
};

// Costo unitario en moneda predeterminada (usa el alternativo × tipo de cambio si no tiene propio).
export const supplyCostDefault = (supply, rate) => {
  const own = toNum(supply?.unit_cost);
  if (own) return own;
  const alt = toNum(supply?.unit_cost_alt);
  const r = validRate(rate);
  if (alt && r) return alt * r;
  return own || 0;
};

// Costo unitario en moneda alternativa (usa el predeterminado ÷ tipo de cambio si no tiene propio).
// Devuelve null si no se puede calcular (sin costo alternativo ni tipo de cambio).
export const supplyCostAlt = (supply, rate) => {
  const alt = toNum(supply?.unit_cost_alt);
  if (alt) return alt;
  const own = toNum(supply?.unit_cost);
  const r = validRate(rate);
  if (r) return (own || 0) / r;
  return alt === 0 ? 0 : null;
};
